import { API_PREFIX, type HttpClient } from "@nasam/kernel";
import type { AuthRepository } from "../domain";
import {
  activationLinkResultFromDto,
  authSessionFromDto,
  loginResultFromDto,
  passwordResetOtpResultFromDto,
} from "./authMappers";
import {
  activationLinkResponseDtoSchema,
  loginResponseDtoSchema,
  messageResponseDtoSchema,
  passwordResetOtpResponseDtoSchema,
  sessionResponseDtoSchema,
} from "./authDtos";

const AUTH_PATH = `${API_PREFIX}/auth`;

export function createHttpAuthRepository(http: HttpClient): AuthRepository {
  const publicConfig = { skipAuth: true } as const;

  return {
    async login(request) {
      const response = await http.post(
        `${AUTH_PATH}/login`,
        request,
        publicConfig,
      );
      return loginResultFromDto(loginResponseDtoSchema.parse(response).data);
    },
    async verifyOtp(request) {
      const response = await http.post(
        `${AUTH_PATH}/verify-otp`,
        request,
        publicConfig,
      );
      return authSessionFromDto(sessionResponseDtoSchema.parse(response).data);
    },
    async resendOtp(request) {
      const response = await http.post(
        `${AUTH_PATH}/resend-otp`,
        request,
        publicConfig,
      );
      messageResponseDtoSchema.parse(response);
    },
    async logout() {
      messageResponseDtoSchema.parse(await http.post(`${AUTH_PATH}/logout`));
    },
    async refreshAccessToken() {
      const response = await http.post(
        `${AUTH_PATH}/refresh-access-token`,
        undefined,
        publicConfig,
      );
      return authSessionFromDto(sessionResponseDtoSchema.parse(response).data);
    },
    async changePassword(request) {
      const response = await http.patch(
        `${AUTH_PATH}/change-password`,
        request,
      );
      messageResponseDtoSchema.parse(response);
    },
    async forgotPassword(request) {
      const response = await http.post(
        `${AUTH_PATH}/forget-password`,
        request,
        publicConfig,
      );
      messageResponseDtoSchema.parse(response);
    },
    async verifyPasswordResetOtp(request) {
      const response = await http.post(
        `${AUTH_PATH}/verify-reset-otp`,
        request,
        publicConfig,
      );
      return passwordResetOtpResultFromDto(
        passwordResetOtpResponseDtoSchema.parse(response),
      );
    },
    async resetPassword(request) {
      const response = await http.patch(
        `${AUTH_PATH}/reset-password`,
        request,
        publicConfig,
      );
      messageResponseDtoSchema.parse(response);
    },
    async checkActivationLink(request) {
      const response = await http.post(
        `${AUTH_PATH}/activation-link-status`,
        request,
        publicConfig,
      );
      return activationLinkResultFromDto(
        activationLinkResponseDtoSchema.parse(response),
      );
    },
    async activateAccount(request) {
      const response = await http.patch(
        `${AUTH_PATH}/activate-account`,
        request,
        publicConfig,
      );
      messageResponseDtoSchema.parse(response);
    },
    /**
     * Pending-contract (see docs/api/gaps.md): the live endpoint at this path
     * requires an authenticated account-admin caller, but this method serves
     * an unauthenticated user on an expired activation link — there is no
     * public self-service variant. Left wired for MSW; will 401/403 against
     * the real API until the backend adds one.
     */
    async resendActivationLink(request) {
      const response = await http.post(
        `${AUTH_PATH}/resend-activation-link`,
        request,
        publicConfig,
      );
      messageResponseDtoSchema.parse(response);
    },
  };
}
